import { Component, ChangeDetectorRef, OnInit, OnDestroy, Input, ElementRef, HostListener, ViewChild, QueryList, ViewChildren, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../apiConnection/ApiService';
import { Urls } from '../../../apiConnection/urls';
import { CommonModule } from '@angular/common';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { DocumentSpecialMeasuresFormComponent } from '../document-special-measures-form/document-special-measures-form.component';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Utils } from '@/utils/utils';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { DocumentAcceptBudgetModalComponent } from '../document-accept-budget-modal/document-accept-budget-modal.component';
import { ClientFormComponent } from '@/pages/clients/client-form/client-form.component';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { DocumentsFormSkeletonComponent } from './documents-form-skeleton/documents-form-skeleton.component';
import { DropdownCoordinatorService } from '@/components/dropdown-coordinator.service';
import { OfflineQueueService } from '@/offline/offline-queue.service';
import { SyncService } from '@/offline/sync.service';
import { WriteQueueEntry } from '@/offline/db';
import { DocumentDeviceMonitorService } from '@/offline/document-device-monitor.service';
import { DocumentIdCache } from '@/utils/document-id-cache';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';

@Component({
  imports: [
    DecimalInputDirective,
    ReactiveFormsModule,
    CommonModule,
    CustomInputComponent,
    CustomSelectComponent,
    RouterModule,
    DocumentSpecialMeasuresFormComponent,
    DocumentAcceptBudgetModalComponent,
    CustomModalComponent,
    DocumentsFormSkeletonComponent,
    RouterModule,
  ],
  templateUrl: './documents-form.page.html'
})
export class DocumentFormPage implements OnInit, OnDestroy {
  get invoiceSplitArray(): FormArray {
    return this.saveForm.get('invoice_split') as FormArray;
  }
  public loading = signal(false);
  public saveForm!: FormGroup;
  public showResults = signal(false);
  public filteredProducts = signal<any[]>([]);
  public idEditMeasures = signal(0);
  public editMeasures = signal(false);
  public showAcceptBudgetModal = signal(false);
  public showProductCatalog = signal(false);
  public showSplitPay = signal(false);
  public isReadonly = signal(false);
  public specialMeasures = signal<{ w: number, h: number, t: number }>({ w: 1, h: 1, t: 1 });
  // Plantilla de medidas propia del producto (p.special_measures), para que cada nueva línea
  // añadida con "+" dentro del modal parta de estos valores igual que la primera línea, en
  // vez de 1/1/1 genéricos — ver DocumentSpecialMeasuresFormComponent.addItem().
  public defaultMeasures = signal<{ w: number, h: number, t: number } | null>(null);
  public dropdowns = signal<any>({
    clients: [],
    users: [],
    payMethods: [],
    products: [],
    tariffs: [],
    offices: [],
    ibans: []
  });
  type = signal<string | null>(null);

  public clientFormComponent = ClientFormComponent

  @Input() id: number | string = 0;

  // hidden items dropdown when click outside
  @ViewChild('mainSearch') searchContainer!: ElementRef;
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (this.searchContainer && !this.searchContainer.nativeElement.contains(event.target)) {
      this.showResults.set(false);
    }
  }

  public pendingSyncEntry = signal<WriteQueueEntry | null>(null);

  private openedSubscription?: Subscription;
  private resolvedSubscription?: Subscription;

  openProductSearch() {
    // stopPropagation() en otros dropdowns (custom-select) impide que este
    // onClickOutside se dispare, así que avisamos explícitamente al abrirnos
    this.coordinator.notifyOpened(this);
    this.showResults.set(true);
  }

  @ViewChildren('productListQuantity') quantityInputs!: QueryList<ElementRef<HTMLInputElement>>;

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.saveForm?.dirty) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private coordinator: DropdownCoordinatorService,
    private offlineQueue: OfflineQueueService,
    private syncService: SyncService,
    private documentDeviceMonitor: DocumentDeviceMonitorService,
  ) {
    // TODO: deprecated, change in the future 😅
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  get isClientSelected() {
    return this.saveForm.get('client_id')?.value > 0
  }

  isDocTypeAllowed(type: string): boolean {
    const allowed = this.authenticationService.getAllowedDocumentTypes();
    return !allowed || allowed.includes(type);
  }

  ngOnInit(): void {
    this.isReadonly.set(!!this.route.snapshot.data['readonly']);

    this.route.params.subscribe(params => {

      this.type.set(params['type'] || 'invoice');
      this.id = params['id'] || 0;

      // Cubre todo el ciclo de carga (selectores + documento) con un único loading,
      // en vez de solo el tramo de getById().
      this.loading.set(true);

      this.createForm();
      this.getDropdowns(() => {
        if (this.id != 0) {
          this.getById(this.id);
        } else {
          this.getIdByCompany();
        }
      });

      this.checkPendingSync();

    })

    // Si un custom-select se abre, cerramos el dropdown de búsqueda de productos
    this.openedSubscription = this.coordinator.opened$.subscribe(source => {
      if (source !== this && this.showResults()) {
        this.showResults.set(false);
      }
    });

    // Si este documento (temp id o pendiente de actualización) termina de sincronizarse
    // mientras el formulario sigue abierto, recargamos contra el id real ya asignado.
    this.resolvedSubscription = this.syncService.resolved$.subscribe(({ tempId, realId, entityType }) => {
      if (entityType === 'documents' && tempId === Number(this.id)) {
        this.refresh(realId);
      }
    });
  }

  ngOnDestroy(): void {
    this.openedSubscription?.unsubscribe();
    this.resolvedSubscription?.unsubscribe();
  }

  private async checkPendingSync(): Promise<void> {
    this.pendingSyncEntry.set(await this.offlineQueue.findPendingForEntity('documents', Number(this.id)) ?? null);
    this.cdr.detectChanges();
  }

  focusOnLastProduct() {
    setTimeout(() => {
      const lastInputRef = this.quantityInputs.last;

      if (lastInputRef && lastInputRef.nativeElement) {
        lastInputRef.nativeElement.focus();
        lastInputRef.nativeElement.select();
      }
    }, 100);
  }

  changeType() {
    const typeControl = this.saveForm.get('type');

    const currentType = typeControl?.value;

    const options: Record<string, string> = {
      'factura': 'invoice',
      'albaran': 'delivery-note',
      'devolucion': 'refund',
      'presupuesto': 'budget'
    }

    const slug = options[currentType] || options['factura'];
    const parent = slug === 'budget' ? 'budgets' : 'sales';
    this.router.navigate(['/' + parent + '/new/' + slug]);
  }

  showSplitPayments() {
    this.showSplitPay.set(!this.showSplitPay());
    this.cdr.detectChanges()
  }

  toggleEditMeasures(specialMeasuresObject: any = '', specialMeasures: string = '') {
    if (specialMeasures) {
      if (typeof specialMeasures == 'string')
        this.specialMeasures.set(JSON.parse(specialMeasures))
    }
    this.idEditMeasures.set(0)
    this.editMeasures.set(!this.editMeasures())
  }

  openMeasures(index: number, currentSavedMeasures: any, productConfig: string) {
    this.idEditMeasures.set(index);

    const productDefaults = JSON.parse(productConfig);
    this.defaultMeasures.set(productDefaults);

    this.specialMeasures.set((currentSavedMeasures && Array.isArray(currentSavedMeasures) && currentSavedMeasures.length > 0)
      ? currentSavedMeasures
      : productDefaults);

    this.editMeasures.set(true);
  }

  acceptMeasures(item: { totalVolume: string, details: any }) {
    const product = this.items.controls[this.idEditMeasures()]
    product.get('special_measures')?.setValue(item.details)
    product.get('quantity')?.setValue(Math.round(Number(item.totalVolume) * 1000) / 1000)

    this.toggleEditMeasures()
    this.cdr.detectChanges()

    setTimeout(() => {
      this.searchContainer?.nativeElement?.focus();
    }, 100);
  }

  createForm() {

    const options: Record<string, string> = {
      'invoice': 'factura',
      'delivery-note': 'albaran',
      'refund': 'devolucion',
      'budget': 'presupuesto'
    }

    const currentType = this.type();
    const type = currentType ? options[currentType] || options['invoice'] : options['invoice'];

    this.saveForm = new FormGroup({
      id_by_company: new FormControl(null),
      year: new FormControl(new Date().getFullYear(), [Validators.required]),
      date: new FormControl(new Date().toISOString().split('T')[0], [Validators.required]),
      phone: new FormControl(''),
      tariff: new FormControl(null),
      pay_id: new FormControl(null),
      tax_included: new FormControl(1),
      user_id: new FormControl(null, [Validators.required]),
      discount_absolute_bool: new FormControl(0),
      discount: new FormControl(0),
      notes: new FormControl(null),
      accept: new FormControl(1),
      client_id: new FormControl(null, [Validators.required]),
      type: new FormControl(type, [Validators.required]),
      irpf_bool: new FormControl(0),
      subtotal: new FormControl('0.00'),
      total: new FormControl('0.00'),
      items: new FormArray([]),
      invoice_split: new FormArray([]),
      invoice_credit_bool: new FormControl(0),
      dailly_invoice_bool: new FormControl(0),
      hold_stock: new FormControl(null),
      office_id: new FormControl(null),
      iban: new FormControl(null)
    });

    if (type === 'presupuesto' && this.authenticationService.currentUserValue.id == 2) {
      this.saveForm.get('hold_stock')?.setValue('1');
    }

    this.saveForm.valueChanges.subscribe(() => this.calculateInvoice());

    this.saveForm.get('tariff')?.valueChanges.subscribe(() => {
      this.updateAllItemsPrices();
    });

    // En un documento nuevo, el número depende de la oficina y el año elegidos
    this.saveForm.get('office_id')?.valueChanges.subscribe(() => {
      if (this.id == 0) this.getIdByCompany();
    });
    this.saveForm.get('year')?.valueChanges.subscribe(() => {
      if (this.id == 0) this.getIdByCompany();
    });
  }

  // tax_included = 0 marca el documento como "sin IVA" (cliente con el checkbox "IVA"
  // desmarcado): no se suma ni se desglosa IVA, el precio sin IVA es el mismo que con IVA
  // (ver isWithoutTax). Con tax_included = 1 (valor normal) decide la tarifa: solo la de
  // ticket (ticket_tariff, `tax: true`) trae el precio con IVA incluido; las invoice_tariffs dan
  // el precio sin IVA y el IVA se suma encima. Sin tarifa (o una que ya no existe en la config)
  // getPrice() cae al precio de ticket, así que se trata igual.
  get isWithoutTax(): boolean {
    return Number(this.saveForm.get('tax_included')?.value) === 0;
  }

  get isTaxIncluded(): boolean {
    const tariff = this.dropdowns().tariffs?.find((t: any) => t.id == this.saveForm.get('tariff')?.value);
    return !tariff || !!tariff.tax;
  }

  get items() { return this.saveForm.get('items') as FormArray; }
  get itemControls() { return this.items.controls; }
  get invoiceSplitConfiguration() {
    if (this.saveForm.get('pay_id')?.value) {
      const payMethod = this.dropdowns().payMethods.filter((pay_method: { id: number }) => pay_method.id == this.saveForm.get('pay_id')?.value)
      if (payMethod.length) {
        return JSON.parse(payMethod[0].configurations);
      }
    }
    return []
  }

  changePayMethod() {
    const config = this.invoiceSplitConfiguration;
    const splitArray = this.saveForm.get('invoice_split') as FormArray;
    splitArray.clear();

    if (config && config.length) {
      config.forEach((c: any) => {
        const baseDateStr = this.saveForm.get('date')?.value;
        const baseDate = baseDateStr ? new Date(baseDateStr + 'T00:00:00') : new Date();
        baseDate.setDate(baseDate.getDate() + Number(c.days));
        const yyyy = baseDate.getFullYear();
        const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
        const dd = String(baseDate.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;
        splitArray.push(new FormGroup({
          date: new FormControl(dateStr),
          percent: new FormControl(c.percent),
          subtotal: new FormControl('0.00'),
          total: new FormControl('0.00'),
        }));
      });
      this.updateInvoiceSplitTotals();
      splitArray.controls.forEach((group: any) => {
        group.get('percent').valueChanges.subscribe(() => {
          this.updateInvoiceSplitTotals();
        });
      });
    }
  }
  updateInvoiceSplitTotals() {
    const subtotal = parseFloat(this.saveForm.get('subtotal')?.value || '0.00');
    const total = parseFloat(this.saveForm.get('total')?.value || '0.00');
    const splitArray = this.saveForm.get('invoice_split') as FormArray;
    splitArray.controls.forEach((group: any) => {
      const percent = parseFloat(group.get('percent').value) / 100;
      const splitSubtotal = (subtotal * percent).toFixed(2);
      const splitTotal = (total * percent).toFixed(2);
      group.get('subtotal').setValue(splitSubtotal, { emitEvent: false });
      group.get('total').setValue(splitTotal, { emitEvent: false });
    });
  }

  getPrice(product: any, tariffId: any = null): number {
    const choosedTariffId = tariffId ?? this.saveForm.get('tariff')?.value;
    const tariff = this.dropdowns().tariffs?.find((t: any) => t.id == choosedTariffId);

    if (!tariff || tariff.tax) {
      return product.price || 0;
    }

    try {
      const more_prices = typeof product.more_prices === 'string'
        ? JSON.parse(product.more_prices)
        : product.more_prices;

      const specificPrice = more_prices?.find((p: any) => p.id == choosedTariffId);
      return specificPrice ? specificPrice.price / 100 : (product.price || 0);
    } catch (e) {
      return product.price || 0;
    }
  }

  updateAllItemsPrices() {
    this.items.controls.forEach(control => {

      const productId = control.get('product_id')?.value;
      const product = this.dropdowns().products.find((p: any) => p.id == productId);

      if (product) {
        const newPrice = this.getPrice(product);
        control.get('price')?.setValue(newPrice, { emitEvent: false });
      }
    });
    this.calculateInvoice();
  }

  getDropdowns(onComplete?: () => void) {
    const config = { users: {}, pay_methods: {}, clients: {}, products: {}, companies: {}, offices: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        const company = data.companies[0];

        const ticketTariff = company.ticket_tariff ? [{ id: company.ticket_tariff, name: `Base (${company.ticket_tariff})`, tax: true }] : []

        const invoice_tariffs = JSON.parse(company.invoice_tariffs || '[]').map((tariff: any) => ({
          id: Number(tariff.id),
          name: `Tarifa ${tariff.id} (${tariff.quotient})`,
          tax: false
        }));

        const next = { ...this.dropdowns() };

        next.tariffs = [
          ...ticketTariff,
          ...invoice_tariffs
        ];

        next.tax_config = company.tax_config ? JSON.parse(company.tax_config) : [];

        const ibans = company.iban
          ? (typeof company.iban === 'string'
            ? JSON.parse(company.iban)
            : company.iban)
          : [];

        next.ibans = ibans;

        const primaryIban = ibans.find((i: any) => i.is_primary);
        if (this.id == 0 && primaryIban) {
          this.saveForm.get('iban')?.setValue(primaryIban.iban);
        }

        next.users = data.users || [];
        next.payMethods = data.pay_methods || [];
        next.clients = data.clients || [];
        next.products = data.products || [];
        next.offices = data.offices || [];

        this.dropdowns.set(next);
        this.filteredProducts.set([...next.products]);

        // Defaults only for new documents: when editing, getById() runs right after this
        // (see ngOnInit) and sets user/office/tariff from the saved document, so setting
        // defaults here would just be overwritten anyway.
        if (this.id == 0) {
          const currentUserId = this.authenticationService.currentUserValue.id
          const userId = next.users.find((user: { id: number }) => user.id == currentUserId)
            ? currentUserId
            : next.users[0]?.id;

          this.saveForm.get('user_id')?.setValue(userId);
          // Con oficinas, la oficina es obligatoria (el backend numera por oficina y lo valida)
          if (next.offices.length) {
            this.saveForm.get('office_id')?.addValidators(Validators.required);
          }
          this.saveForm.get('office_id')?.setValue(next.offices[0]?.id ?? null);

          if (invoice_tariffs.length > 0) {
            this.saveForm.get('tariff')?.patchValue(invoice_tariffs[0].id, { emitEvent: true });
          }
        }

        this.cdr.detectChanges();
      },
      complete: () => {
        onComplete?.();
      }
    });
  }

  filterProducts(query: string) {
    if (!query) {
      this.filteredProducts.set([]);
      return;
    }

    this.openProductSearch();

    const q = query.toLowerCase();
    this.filteredProducts.set(this.dropdowns().products.filter((p: any) =>
      p.name.toLowerCase().includes(q) ||
      p.id.toString().includes(q) ||
      (p.code && p.code.toLowerCase().includes(q))
    ));
  }

  get catalogProducts(): any[] {
    return [...this.dropdowns().products].sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  openProductCatalog() {
    this.showProductCatalog.set(true);
  }

  closeProductCatalog() {
    this.showProductCatalog.set(false);
  }

  selectProductFromCatalog(product: any) {
    this.selectProductFromSearch(product, this.searchContainer.nativeElement);
    this.showProductCatalog.set(false);
  }

  addFirstProduct(inputElement: HTMLInputElement) {
    if (this.filteredProducts().length > 0) {
      const firstProduct = this.filteredProducts()[0];
      this.selectProductFromSearch(firstProduct, inputElement);
    }
  }

  onSearchEnter(event: Event, inputElement: HTMLInputElement) {
    event.preventDefault();
    // Este Enter ya gestiona su propio foco (al producto/medidas recién añadidos); evitamos
    // que el handler global de "Enter avanza como Tab" (app.ts) lo pise después.
    event.stopPropagation();

    if (this.filteredProducts().length === 0) {
      return;
    }

    this.addFirstProduct(inputElement);
    this.showResults.set(false);
    // selectProductFromSearch (called from addFirstProduct) already focuses the quantity
    // input, or opens the special-measures editor instead when the product needs it.
  }

  selectProductFromSearch(product: any, inputElement: HTMLInputElement) {
    const priceWithTariff = this.getPrice(product);

    this.addItem({
      id: null,
      product_id: product.id,
      name: product.name,
      personalized_desc: null,
      price: priceWithTariff,
      quantity: 1,
      discount: 0,
      special_measures: [],
      tax: this.getProductTaxQuotient(product)
    });

    inputElement.value = '';
    inputElement.focus()
    this.showResults.set(false);
    this.filteredProducts.set(this.dropdowns().products);

    if (product.special_measures) {
      this.openMeasures(this.items.length - 1, [], product.special_measures);
    } else {
      this.focusOnLastProduct()
    }
  }

  addItem(itemData: any = null) {
    if (itemData && itemData.product_id) {
      const existingItemIndex = this.items.controls.findIndex(
        control => control.get('product_id')?.value == itemData.product_id
      );

      // if (existingItemIndex !== -1) {
      //   const control = this.items.at(existingItemIndex);
      //   const currentQty = Number(control.get('quantity')?.value || 0);
      //   control.get('quantity')?.setValue(currentQty + 1);
      //   return;
      // }
    }

    const itemGroup = new FormGroup({
      id: new FormControl(itemData?.id || null),
      product_id: new FormControl(itemData?.product_id || null, [Validators.required]),
      name: new FormControl(itemData?.name || 'Nuevo artículo'),
      personalized_desc: new FormControl(itemData?.personalized_desc || null),
      quantity: new FormControl(itemData?.quantity || 1, [Validators.required, Validators.min(1)]),
      discount: new FormControl(itemData?.discount || 0),
      price: new FormControl(itemData?.price || 0),
      special_measures: new FormControl(itemData?.special_measures || null),
      tax: new FormControl(itemData?.tax ?? null)
    });
    this.items.push(itemGroup);
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.calculateInvoice();
  }

  calculateInvoice() {
    let sub = 0;
    let tot = 0;

    this.items.controls.forEach(c => {
      sub += Number(this.getAndCalculateWithOutTax(c));
      tot += Number(this.getAndCalculateWithTax(c));
    });

    const disc = this.saveForm.get('discount')?.value || 0;
    const isAbsolute = Number(this.saveForm.get('discount_absolute_bool')?.value) === 1;

    const lastSub = sub;

    if (isAbsolute) {
      sub -= disc;
    } else {
      sub *= (1 - (disc / 100));
    }

    tot = sub * tot / lastSub;

    if (isNaN(tot)) {
      tot = 0;
    }

    if (this.saveForm.get('irpf_bool')?.value) {
      tot += (sub * 0.052);
    }

    this.saveForm.get('subtotal')?.setValue(sub.toFixed(2), { emitEvent: false });
    this.saveForm.get('total')?.setValue(tot.toFixed(2), { emitEvent: false });
    if ((this.saveForm.get('invoice_split') as FormArray).length > 0) {
      this.updateInvoiceSplitTotals();
    }
  }

  showOfficeName() {
    return this.dropdowns().offices.find((o: any) => o.id == this.saveForm.get('office_id')?.value)?.name;
  }

  showIbanLabel() {
    const val = this.saveForm.get('iban')?.value;
    const found = this.dropdowns().ibans.find((i: any) => i.iban === val);
    return found ? found.label : (val ? val : '—');
  }

  // El backend numera por oficina + año + tipo: hay que pedir el número con la misma
  // oficina y año con que se va a guardar, o el formulario muestra otro distinto.
  private idByCompanyRequest = 0;

  getIdByCompany() {
    const params = new URLSearchParams();
    const officeId = this.saveForm.get('office_id')?.value;
    const year = this.saveForm.get('year')?.value;
    if (officeId) params.set('office_id', officeId);
    if (year) params.set('year', year);

    // Si cambian oficina/año con una petición en curso, solo vale la última respuesta
    const request = ++this.idByCompanyRequest;

    this.apiService.getPetition(Urls.urls.documents + '/last-id-by-company/' + this.type(), '?' + params.toString()).subscribe({
      next: (data: any) => {
        if (request !== this.idByCompanyRequest) return;
        this.saveForm.patchValue({ id_by_company: data.last_id_by_company + 1 }, { emitEvent: false });
        this.loading.set(false);
        this.cdr.detectChanges();
      },
      // Sin conexión y sin esa combinación tipo/oficina/año en caché: el número lo asigna el
      // backend al sincronizar (id_by_company es un campo calculado en servidor), así que el
      // formulario no debe quedarse cargando.
      error: () => {
        if (request !== this.idByCompanyRequest) return;
        this.saveForm.patchValue({ id_by_company: null }, { emitEvent: false });
        this.loading.set(false);
        this.cdr.detectChanges();
      },
    });
  }

  getById(id: number | string) {
    this.apiService.getById(Urls.urls.documents, id).subscribe({
      next: (data: any) => {
        const doc = data.document || {};

        const savedSubtotal = (doc.subtotal !== undefined && doc.subtotal !== null) ? (Number(doc.subtotal) / 100).toFixed(2) : '0.00';
        const savedTotal = (doc.total !== undefined && doc.total !== null) ? (Number(doc.total) / 100).toFixed(2) : '0.00';

        this.saveForm.patchValue({
          id_by_company: doc.id_by_company ?? null,
          year: doc.year ?? new Date().getFullYear(),
          date: doc.date ? doc.date.split(' ')[0] : (doc.date || new Date().toISOString().split('T')[0]),
          phone: doc.phone || doc.phone_1 || '',
          tariff: doc.tariff ?? null,
          pay_id: doc.pay_id ?? null,
          // 0 = sin IVA (precio sin IVA = precio con IVA), ver isWithoutTax. Se conserva tal
          // cual al guardar; null/ausente cuenta como 1 (normal).
          tax_included: (doc.tax_included === 0 || doc.tax_included === false || doc.tax_included === '0') ? 0 : 1,
          user_id: doc.user_id ?? this.authenticationService.currentUserValue.id,
          discount_absolute_bool: doc.discount_absolute_bool ?? 0,
          discount: doc.discount ?? 0,
          notes: doc.notes ?? null,
          accept: doc.accept ?? 1,
          client_id: doc.client_id ?? null,
          type: doc.type ?? this.saveForm.get('type')?.value,
          irpf_bool: doc.irpf_bool ?? 0,
          subtotal: savedSubtotal,
          total: savedTotal,
          invoice_credit_bool: doc.invoice_credit_bool ?? 0,
          dailly_invoice_bool: doc.dailly_invoice_bool ?? 0,
          hold_stock: ((doc.type ?? this.saveForm.get('type')?.value) === 'presupuesto' && this.authenticationService.currentUserValue.id == 2)
            ? '1'
            : (doc.hold_stock ?? null),
          office_id: doc.office_id ?? null,
          iban: doc.iban ?? null,
          status: null
        });

        // Populate items from document details or products
        const details = data.document.document_details || data.document.products || [];
        const itemsArray = this.saveForm.get('items') as FormArray;
        // clear existing
        while (itemsArray.length) {
          itemsArray.removeAt(0);
        }

        details.forEach((d: any) => {
          const pivot = d.pivot || {};
          itemsArray.push(new FormGroup({
            id: new FormControl(pivot.document_id || null),
            product_id: new FormControl(d.id || pivot.product_id || null, [Validators.required]),
            name: new FormControl(pivot.personalized_desc || d.name || 'Nuevo artículo'),
            personalized_desc: new FormControl(pivot.personalized_desc ?? null),
            quantity: new FormControl(pivot.quantity ?? 1, [Validators.required, Validators.min(1)]),
            discount: new FormControl(pivot.discount ?? 0),
            price: new FormControl((pivot.price !== undefined && pivot.price !== null) ? (Number(pivot.price) / 100) : (d.price ?? 0)),
            special_measures: new FormControl(this.parseSpecialMeasures(pivot.special_measures)),
            tax: new FormControl(pivot.tax ?? this.getProductTaxQuotient(d))
          }));
        });

        // Patching 'subtotal'/'total' above and pushing each item both re-trigger
        // saveForm.valueChanges -> calculateInvoice(), which overwrites them with a
        // client-side recompute. Restore the values actually saved on the document.
        this.saveForm.get('subtotal')?.setValue(savedSubtotal, { emitEvent: false });
        this.saveForm.get('total')?.setValue(savedTotal, { emitEvent: false });

        if (this.isReadonly()) {
          this.saveForm.disable({ emitEvent: false });
        }

        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('ERROR obtener productos. ' + error);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  /**
   * Reglas que el backend aplica a todo guardado que no es borrador (al menos una línea; método de
   * pago en facturas). Comprobarlas aquí evita que, sin conexión, se encole algo que fallará al sincronizar.
   */
  private meetsFinalSaveRules(type: string = this.saveForm.get('type')?.value): boolean {
    if (this.items.length === 0) {
      Utils.notification.alert('Añade al menos un producto antes de guardar.');
      return false;
    }
    if (type === 'factura' && !this.saveForm.get('pay_id')?.value) {
      Utils.notification.alert('El método de pago es obligatorio para las facturas.');
      return false;
    }
    return true;
  }

  // Albaranes y presupuestos no se guardan como borrador con "Guardar"; el
  // presupuesto tiene su propio botón "Borrador" que llama con asDraft = true.
  saveEntity(asDraft: boolean = false) {
    if (this.saveForm.valid) {
      if (!this.documentDeviceMonitor.canGenerateOffline()) {
        Utils.notification.alert('Sin conexión: este ordenador no está autorizado para generar documentos sin internet.');
        return;
      }

      const savesAsFinal = ['albaran', 'presupuesto'].includes(this.saveForm.get('type')?.value);
      const status = asDraft || !savesAsFinal ? 'draft' : null;
      if (status !== 'draft' && !this.meetsFinalSaveRules()) return;

      this.loading.set(true);
      const payload = this.buildSavePayload(status);

      this.apiService.save(Urls.urls.documents, payload, this.id).subscribe({
        next: (entity) => {
          Utils.notification.success(payload.type + " se ha creado correctamente")
          this.saveForm.markAsPristine();
          DocumentIdCache.record('documents', payload.type, entity.document.id, entity.document.id_by_company);
          this.refresh(entity.document.id)
        },
        error: (error: any) => {
          this.loading.set(false)
          Utils.notification.alert(Utils.extractErrorMessage(error) || "Error al guardar " + payload.type)
          this.cdr.detectChanges()
        },
        complete: () => {
          this.loading.set(false)
        }
      });
    }
  }

  // Shared by saveEntity() (draft) and saveAndPrint() (final) so both send the same line data.
  private buildSavePayload(status: 'draft' | null) {
    const payload = {
      ...this.saveForm.value,
      status
    };

    payload.subtotal = Math.abs(payload.subtotal * 100)
    payload.total = Math.abs(payload.total * 100)

    // Copia de cada línea: saveForm.value devuelve los mismos objetos en cada llamada, así que
    // mutarlos haría que una segunda llamada (acceptBudget) volviera a multiplicar el precio por 100.
    payload.items = payload.items.map((item: any) => {
      // Persist a manually-edited line name as personalized_desc; if it still
      // matches the underlying product's real name, don't store an override.
      const originalName = (this.getProductInfo(item.product_id)?.name || '').trim();
      const currentName = (item.name || '').trim();

      return {
        ...item,
        special_measures: item.special_measures || null,
        price: Math.abs(item.price * 100),
        personalized_desc: (currentName && currentName !== originalName) ? currentName : null,
      };
    })

    const invoice_split = Array.isArray(payload.invoice_split) ? payload.invoice_split : [];
    payload.invoice_split = invoice_split.length ? invoice_split : null;

    return payload;
  }

  refresh(id: number) {
    let currentUrl = this.router.url;

    const currentType = this.type();
    if (id && currentType && ['delivery-note', 'budget'].includes(currentType)) {
      const parent = currentType === 'budget' ? 'budgets' : 'sales';
      currentUrl = '/' + parent + '/edit/' + id
    }

    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  changeClient() {
    const id = this.saveForm.get('client_id')?.value;
    const client = this.dropdowns().clients.find((c: any) => c.id == id);
    if (client) {
      this.saveForm.get('phone')?.setValue(client.phone)
      this.saveForm.get('tariff')?.setValue(client.tariff_id)
      this.saveForm.get('pay_id')?.setValue(client.payment_method_id)
      // Recargo de equivalencia (+5,2%, guardado en irpf_bool): ya no hay checkbox en el
      // formulario, se aplica siempre según el flag equivalence_recharge del cliente.
      this.saveForm.get('irpf_bool')?.setValue(client.equivalence_recharge ? 1 : 0)
      // Cliente con el checkbox "IVA" desmarcado: tax_included = 0, sin IVA (el precio sin
      // IVA es el mismo que con IVA, ver isWithoutTax). Se guarda en el documento, así que cambiar luego el flag
      // del cliente no altera documentos ya creados. Sin valor cuenta como IVA normal.
      this.saveForm.get('tax_included')?.setValue(client.iva === false || client.iva === 0 || client.iva === '0' ? 0 : 1)
      if (client.discount) {
        this.saveForm.get('discount')?.setValue(client.discount)
        this.saveForm.get('discount_absolute_bool')?.setValue(0) // percentage
      }

      this.searchContainer.nativeElement.focus()
    }
  }

  private parseSpecialMeasures(value: any): any[] | null {
    if (!value) return null;
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  getProductInfo(productId: any) {
    if (!productId || !this.dropdowns().products) return null;
    return this.dropdowns().products.find((p: any) => p.id == productId);
  }

  getProductTaxQuotient(product: any): number | null {
    if (!product || !this.dropdowns().tax_config) return null;
    const taxConfig = this.dropdowns().tax_config.find((t: any) => t.id == product.tax_type);
    return taxConfig?.quotient ? Number(taxConfig.quotient) : null;
  }

  saveAndPrint() {
    if (this.saveForm.valid) {
      if (!this.documentDeviceMonitor.canGenerateOffline()) {
        Utils.notification.alert('Sin conexión: este ordenador no está autorizado para generar documentos sin internet.');
        return;
      }
      if (!this.meetsFinalSaveRules()) return;

      this.loading.set(true);
      const payload = this.buildSavePayload(null);

      this.apiService.save(Urls.urls.documents, payload, this.id).subscribe({
        next: (result: any) => {
          const id = result.document.id;
          DocumentIdCache.record('documents', payload.type, id, result.document.id_by_company);
          window.open(`/invoice/print/${id}`, '_blank');
          this.saveForm.markAsPristine();
          this.refresh(id)
        },
        error: (error: any) => {
          this.loading.set(false)
          Utils.notification.alert(Utils.extractErrorMessage(error) || "Error al guardar " + payload.type)
          this.cdr.detectChanges()
        }
      });
    }
  }

  getItemQuotient(item: any): number {
    const product_id = item.get('product_id')?.value;
    const product = this.dropdowns().products.find((p: any) => p.id == product_id);
    const taxConfig = this.dropdowns().tax_config.find((t: any) => t.id == product?.tax_type);
    return taxConfig && taxConfig.quotient ? Number(taxConfig.quotient) : 21;
  }

  getAndCalculateWithOutTax(item: any) {
    const quantity = item.get('quantity')?.value || 0
    const price = item.get('price')?.value || 0
    const discount = (100 - (item.get('discount')?.value || 0)) / 100

    const lineTotal = quantity * price * discount;

    if (this.isWithoutTax) {
      return lineTotal.toFixed(2)
    }

    if (this.isTaxIncluded) {
      const quotient = this.getItemQuotient(item);
      return (lineTotal / ((quotient + 100) / 100)).toFixed(2)
    }

    return lineTotal.toFixed(2)
  }

  getAndCalculateWithTax(item: any) {
    const quantity = item.get('quantity')?.value || 0
    const price = item.get('price')?.value || 0
    const discount = (100 - (item.get('discount')?.value || 0)) / 100

    const lineTotal = quantity * price * discount;

    if (this.isWithoutTax || this.isTaxIncluded) {
      return lineTotal.toFixed(2)
    }

    const quotient = this.getItemQuotient(item);
    return (lineTotal * ((quotient + 100) / 100)).toFixed(2)
  }

  showSeller() {
    return this.dropdowns().users.find((u: any) => u.id === this.saveForm.get('user_id')?.value)?.name
  }

  onDiscountTab(event: KeyboardEvent, isLast: boolean, searchInput: HTMLInputElement): void {
    if (isLast) {
      event.preventDefault();
      searchInput.focus();
    }
  }

  onSearchProductTab(event: Event, isLast: boolean): void {
    if (isLast) {
      this.showResults.set(false);
    }
  }

  openAcceptBudgetModal() {
    this.showAcceptBudgetModal.set(true);
  }

  closeAcceptBudgetModal() {
    this.showAcceptBudgetModal.set(false);
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.saveForm?.dirty) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      Utils.notification.confirm(
        '¿Estás seguro que quieres abandonar sin guardar?',
        () => resolve(true),
        () => resolve(false),
        { labels: { confirm: 'Cambios sin guardar', confirmOk: 'Salir sin guardar', confirmCancel: 'Seguir editando' } }
      );
    });
  }

  acceptBudget(newType: 'factura' | 'albaran' | 'devolucion', date: string) {
    if (!this.saveForm.valid) return;

    if (!this.documentDeviceMonitor.canGenerateOffline()) {
      Utils.notification.alert('Sin conexión: este ordenador no está autorizado para generar documentos sin internet.');
      return;
    }
    if (!this.meetsFinalSaveRules(newType)) return;

    this.loading.set(true);
    this.showAcceptBudgetModal.set(false);

    const savePayload = this.buildSavePayload(null);

    this.apiService.save(Urls.urls.documents, savePayload, this.id).subscribe({
      next: (response: any) => {
        console.log(response)
        const newPayload = {
          ...this.buildSavePayload(null),
          type: newType,
          date,
        };
        newPayload.items = newPayload.items.map((item: any) => ({ ...item, id: null }));
        newPayload.reference = response.document.id;

        this.apiService.save(Urls.urls.documents, newPayload, 0).subscribe({
          next: (result: any) => {
            const labels: Record<string, string> = { factura: 'Factura', albaran: 'Albarán', devolucion: 'Devolución' };
            const label = labels[newType] || newType;
            Utils.notification.success(label + ' creado correctamente desde presupuesto');
            this.saveForm.markAsPristine();
            DocumentIdCache.record('documents', newType, result.document.id, result.document.id_by_company);
            this.router.navigate(['/invoice/view/' + result.document.id]);
          },
          error: (error: any) => {
            this.loading.set(false);
            Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al crear ' + newType);
            this.cdr.detectChanges();
          },
          complete: () => {
            this.loading.set(false);
          }
        });
      },
      error: (error: any) => {
        this.loading.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar presupuesto');
        this.cdr.detectChanges();
      }
    });
  }
}
